/** HUD component showing a Dragon Warrior-style scrolling event log of combat, XP, and skill events. */
import Phaser from 'phaser';
import { crispText, crispRoundedRect, CRISP_SCALE } from './CrispUtil';
import { TextStyle, Hex, Color } from './Theme';
import { SF0_X, SF0_Y, GAME_WIDTH, GAME_HEIGHT } from '../utils/Constants';

/** Resting entry count — determines the baseline panel height. */
const REST_LINES = 6;
/** Hard cap on entries kept in memory to bound pool size. */
const MAX_ENTRIES = 18;
/** How long each line persists before fading (ms). */
const LINE_LIFETIME_MS = 20000;
/** Panel positioning — bottom-right, flush with hotbar bottom edge. */
const PANEL_WIDTH = 220;
const PADDING = 4;
const MARGIN_RIGHT = 14;
const PANEL_X = SF0_X + GAME_WIDTH - PANEL_WIDTH - MARGIN_RIGHT;
const PANEL_BOTTOM = SF0_Y + GAME_HEIGHT - 10;
const BG_ALPHA = 0.35;
const TEXT_ALPHA = 0.75;

/** Resting panel height — the baseline size with no overflow. */
const LINE_HEIGHT_ESTIMATE = 9;
const REST_HEIGHT = REST_LINES * LINE_HEIGHT_ESTIMATE + PADDING * 2;
/** Maximum panel height — resting size × 2 plus 5 extra lines for overflow. */
const MAX_HEIGHT = REST_HEIGHT * 2 + 5 * LINE_HEIGHT_ESTIMATE;
/** How fast the displayed height lerps toward the target (0–1 per frame). */
const ACCORDION_SPEED = 0.12;

interface CrawlEntry {
  text: string;
  color: string;
  timestamp: number;
}

/** Pooled scrolling event log for combat hits, XP gains, skill activations, and kills. Reuses objects to avoid GPU texture churn. */
export class CrawlOverlay {
  private scene: Phaser.Scene;
  private entries: CrawlEntry[] = [];
  private textPool: Phaser.GameObjects.Text[] = [];
  private activeTextCount = 0;
  private bg: Phaser.GameObjects.Image | null = null;
  private lastBgHeight = 0;
  /** The current smoothed display height of the panel. */
  private displayedHeight = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Add a new entry to the crawl log. */
  push(text: string, color: string = Hex.textPrimary): void {
    this.entries.push({ text, color, timestamp: Date.now() });
    if (this.entries.length > MAX_ENTRIES) {
      this.entries = this.entries.slice(-MAX_ENTRIES);
    }
  }

  /** Update the crawl panel. Called each frame by UIScene. */
  draw(): void {
    // Prune expired entries
    const now = Date.now();
    this.entries = this.entries.filter(e => now - e.timestamp < LINE_LIFETIME_MS);

    if (this.entries.length === 0) {
      for (let i = 0; i < this.activeTextCount; i++) {
        this.textPool[i].setVisible(false);
      }
      this.activeTextCount = 0;
      // Collapse toward zero when empty
      this.displayedHeight += (0 - this.displayedHeight) * ACCORDION_SPEED;
      if (this.displayedHeight < 1) {
        this.displayedHeight = 0;
        if (this.bg) this.bg.setVisible(false);
      }
      return;
    }

    // Show all live entries — the accordion accommodates overflow beyond REST_LINES
    const visible = this.entries;

    // Measure content: set text on each pool entry and accumulate display heights
    let contentHeight = 0;
    for (let i = 0; i < visible.length; i++) {
      const entry = visible[i];
      const txt = this.getOrCreateText(i);
      if (txt.text !== entry.text) txt.setText(entry.text);
      if (txt.style.color !== entry.color) txt.setColor(entry.color);
      contentHeight += txt.displayHeight + 1;
    }
    if (contentHeight > 0) contentHeight -= 1;

    // Target height: fits content exactly, up to MAX_HEIGHT for overflow
    const targetH = Math.min(contentHeight + PADDING * 2, MAX_HEIGHT);

    // Smooth accordion — lerp displayed height toward target
    this.displayedHeight += (targetH - this.displayedHeight) * ACCORDION_SPEED;
    // Snap when close enough to avoid perpetual sub-pixel drift
    if (Math.abs(this.displayedHeight - targetH) < 0.5) this.displayedHeight = targetH;

    const panelH = this.displayedHeight;
    const panelY = PANEL_BOTTOM - panelH;

    // Rebuild background only when rounded height changes
    const roundedH = Math.round(panelH);
    if (roundedH !== this.lastBgHeight) {
      if (this.bg) { this.bg.destroy(); this.bg = null; }
      this.lastBgHeight = roundedH;
    }
    if (!this.bg) {
      this.bg = crispRoundedRect(
        this.scene,
        PANEL_X + PANEL_WIDTH / 2, panelY + panelH / 2,
        PANEL_WIDTH, roundedH, 4, Color.black, BG_ALPHA,
      ).setScrollFactor(0).setDepth(99);
    } else {
      this.bg.setPosition(PANEL_X + PANEL_WIDTH / 2, panelY + panelH / 2);
    }
    this.bg.setVisible(true);

    // Position text bottom-anchored: content sits at the bottom of the panel
    const contentStartY = PANEL_BOTTOM - PADDING - contentHeight;
    let y = contentStartY;
    for (let i = 0; i < visible.length; i++) {
      const entry = visible[i];
      const age = now - entry.timestamp;
      const fade = age > LINE_LIFETIME_MS * 0.7
        ? 1 - (age - LINE_LIFETIME_MS * 0.7) / (LINE_LIFETIME_MS * 0.3) : 1;

      const txt = this.textPool[i];
      // Clip: hide lines that would overflow above the panel top
      if (y < panelY) {
        txt.setVisible(false);
      } else {
        txt.setPosition(PANEL_X + PADDING, y);
        txt.setAlpha(Math.max(0, fade) * TEXT_ALPHA);
        txt.setVisible(true);
      }
      y += txt.displayHeight + 1;
    }

    // Hide unused pool entries
    for (let i = visible.length; i < this.activeTextCount; i++) {
      this.textPool[i].setVisible(false);
    }
    this.activeTextCount = visible.length;
  }

  private getOrCreateText(index: number): Phaser.GameObjects.Text {
    if (index < this.textPool.length) return this.textPool[index];
    const wrapWidth = (PANEL_WIDTH - PADDING * 2) * CRISP_SCALE;
    const txt = crispText(
      this.scene, 0, 0, '',
      { ...TextStyle.crawlPanel, color: Hex.textPrimary,
        wordWrap: { width: wrapWidth, useAdvancedWrap: true } },
    ).setScrollFactor(0).setDepth(100);
    this.textPool.push(txt);
    return txt;
  }

  /** Hide all displayed objects. */
  clear(): void {
    for (let i = 0; i < this.activeTextCount; i++) {
      this.textPool[i].setVisible(false);
    }
    this.activeTextCount = 0;
    if (this.bg) this.bg.setVisible(false);
  }

  /** Destroy all pooled objects and free resources. */
  destroy(): void {
    for (const t of this.textPool) t.destroy();
    this.textPool = [];
    this.activeTextCount = 0;
    if (this.bg) {
      this.bg.destroy();
      this.bg = null;
    }
    this.entries = [];
  }
}
